import { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, LogOut, Loader2, Users, Mail, Calendar, MessageSquare, Search, Eye, RefreshCw, X, Download } from 'lucide-react';
import { toast } from 'sonner';
import { adminLogin, adminMe, adminGetStats, adminGetContacts, adminGetNewsletter, adminGetBookings, adminGetChats, adminGetChatDetail } from '@/lib/api';
import SEO from '@/components/SEO';

const TOKEN_KEY = 'cauratech_admin_token';

const LoginCard = ({ onAuth }) => {
  const [pwd, setPwd] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!pwd.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const r = await adminLogin(pwd.trim());
      if (r.token) {
        try {
          localStorage.setItem(TOKEN_KEY, r.token);
        } catch (storageErr) {
          console.warn('[Admin] localStorage write failed:', storageErr?.message || storageErr);
        }
        onAuth(r.token);
      }
    } catch (err) {
      console.warn('[Admin] login failed:', err?.response?.status, err?.response?.data?.detail || err?.message);
      setError('Contraseña incorrecta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="bg-grid relative min-h-[80vh] grid place-items-center px-6" data-testid="admin-login">
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        onSubmit={submit}
        className="gradient-border w-full max-w-md"
      >
        <div className="inner p-8 sm:p-10">
          <div className="flex flex-col items-center text-center">
            <div className="h-12 w-12 rounded-2xl bg-[rgba(5,126,243,.12)] border border-[rgba(5,126,243,.4)] flex items-center justify-center text-[rgb(var(--brand-electric))]">
              <Lock size={22} />
            </div>
            <h1 className="mt-5 font-display text-2xl font-[700] text-white">Cauratech Admin</h1>
            <p className="mt-1 text-sm text-white/60">Acceso restringido. Identifícate para continuar.</p>
          </div>
          <div className="mt-6">
            <label className="kicker-mono block mb-2 text-white/60">Contraseña</label>
            <input
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="••••••••••"
              className="field"
              autoFocus
              data-testid="admin-password"
            />
            {error && <div className="mt-2 text-xs text-rose-400">{error}</div>}
          </div>
          <button type="submit" disabled={loading || !pwd.trim()} className="btn-primary w-full mt-6 disabled:opacity-50" data-testid="admin-login-submit">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Lock size={14} />}
            Entrar
          </button>
          <Link to="/" className="block text-center mt-4 text-xs text-white/50 hover:text-white">← Volver al sitio</Link>
        </div>
      </motion.form>
    </main>
  );
};

const Tab = ({ active, onClick, icon: Icon, label, count, testId }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
      active ? 'bg-[rgba(5,126,243,.15)] text-white border border-[rgba(5,126,243,.45)]' : 'text-white/60 hover:text-white border border-transparent'
    }`}
    data-testid={testId}
  >
    <Icon size={16} />
    {label}
    {typeof count === 'number' && (
      <span className="ml-1 text-[10px] font-mono-accent bg-white/5 px-1.5 py-0.5 rounded-full text-white/70">{count}</span>
    )}
  </button>
);

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
};

// CSV export helpers
const csvEscape = (val) => {
  if (val === null || val === undefined) return '';
  const s = typeof val === 'string' ? val : String(val);
  // Quote if contains comma, quote, newline or carriage return
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadCSV = (filename, headers, rows) => {
  const lines = [headers.map(csvEscape).join(',')];
  rows.forEach((r) => lines.push(r.map(csvEscape).join(',')));
  // Prepend BOM for Excel UTF-8 compatibility
  const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 800);
};

const fileStamp = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
};

const Dashboard = ({ token, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [chatDetail, setChatDetail] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    let unauthorized = false;
    const handle401 = (err) => {
      if (err?.response?.status === 401) unauthorized = true;
      return null;
    };
    try {
      const [s, c, n, b, ch] = await Promise.all([
        adminGetStats(token).catch(handle401),
        adminGetContacts(token).catch((e) => { handle401(e); return { items: [] }; }),
        adminGetNewsletter(token).catch((e) => { handle401(e); return { items: [] }; }),
        adminGetBookings(token).catch((e) => { handle401(e); return { items: [] }; }),
        adminGetChats(token).catch((e) => { handle401(e); return { items: [] }; }),
      ]);
      if (unauthorized) {
        toast.error('Sesión expirada. Inicia sesión de nuevo.');
        onLogout();
        return;
      }
      setStats(s);
      setContacts(c?.items || []);
      setNewsletter(n?.items || []);
      setBookings(b?.items || []);
      setChats(ch?.items || []);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, []);

  const openChat = async (sid) => {
    setChatLoading(true);
    try {
      const r = await adminGetChatDetail(token, sid);
      setChatDetail({ session_id: sid, messages: r.messages || [] });
    } catch {
      setChatDetail({ session_id: sid, messages: [] });
    } finally {
      setChatLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return { contacts, newsletter, bookings, chats };
    return {
      contacts: contacts.filter((x) => Object.values(x).some((v) => (v || '').toString().toLowerCase().includes(q))),
      newsletter: newsletter.filter((x) => (x.email || '').toLowerCase().includes(q)),
      bookings: bookings.filter((x) => Object.values(x).some((v) => (v || '').toString().toLowerCase().includes(q))),
      chats: chats.filter((x) => `${x.first_message} ${x.last_message} ${x.session_id}`.toLowerCase().includes(q)),
    };
  }, [search, contacts, newsletter, bookings, chats]);

  const exportCurrent = () => {
    const stamp = fileStamp();
    if (tab === 'contacts') {
      downloadCSV(
        `cauratech-contactos-${stamp}.csv`,
        ['Fecha', 'Nombre', 'Email', 'Empresa', 'Mensaje', 'Idioma'],
        filtered.contacts.map((x) => [formatDate(x.created_at), x.name, x.email, x.company || '', x.message, x.language || 'es']),
      );
    } else if (tab === 'newsletter') {
      downloadCSV(
        `cauratech-newsletter-${stamp}.csv`,
        ['Fecha', 'Email', 'Idioma'],
        filtered.newsletter.map((x) => [formatDate(x.created_at), x.email, x.language || 'es']),
      );
    } else if (tab === 'bookings') {
      downloadCSV(
        `cauratech-reservas-${stamp}.csv`,
        ['Fecha', 'Servicio', 'Día', 'Hora', 'Nombre', 'Email', 'Empresa', 'Teléfono', 'Notas', 'Estado', 'Idioma'],
        filtered.bookings.map((x) => [
          formatDate(x.created_at), x.service, x.date, x.time, x.name, x.email,
          x.company || '', x.phone || '', x.notes || '', x.status || 'pending', x.language || 'es',
        ]),
      );
    } else if (tab === 'chats') {
      downloadCSV(
        `cauratech-chats-${stamp}.csv`,
        ['Última actividad', 'Sesión', 'Mensajes', 'Idioma', 'Primer mensaje', 'Último mensaje'],
        filtered.chats.map((x) => [formatDate(x.last_ts), x.session_id, x.count, x.lang || 'es', x.first_message, x.last_message]),
      );
    }
  };

  return (
    <main className="min-h-[80vh] py-8 sm:py-12" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <div className="kicker-mono mb-1">// Cauratech Admin</div>
            <h1 className="font-display text-2xl sm:text-3xl font-[700] text-white">Panel de control</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={refresh} className="btn-ghost" data-testid="admin-refresh" title="Refrescar">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={onLogout} className="btn-ghost" data-testid="admin-logout">
              <LogOut size={14} /> Salir
            </button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          {stats && [
            { k: 'contacts', label: 'Contactos', val: stats.contacts, icon: Users },
            { k: 'newsletter', label: 'Newsletter', val: stats.newsletter, icon: Mail },
            { k: 'bookings', label: 'Reservas', val: stats.bookings, icon: Calendar },
            { k: 'chat_sessions', label: 'Sesiones IA', val: stats.chat_sessions, icon: MessageSquare },
            { k: 'chat_messages', label: 'Mensajes IA', val: stats.chat_messages, icon: MessageSquare },
          ].map((s) => (
            <div key={s.k} className="rounded-2xl border border-white/10 bg-[rgb(var(--bg-1))] p-4" data-testid={`stat-${s.k}`}>
              <div className="flex items-center justify-between">
                <span className="kicker-mono text-white/50">{s.label}</span>
                <s.icon size={14} className="text-[rgb(var(--brand-electric))]" />
              </div>
              <div className="font-display text-3xl font-[700] text-white mt-1">{s.val ?? '—'}</div>
            </div>
          ))}
        </div>

        {/* Tabs + search */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Tab active={tab === 'contacts'} onClick={() => setTab('contacts')} icon={Users} label="Contactos" count={contacts.length} testId="tab-contacts" />
          <Tab active={tab === 'newsletter'} onClick={() => setTab('newsletter')} icon={Mail} label="Newsletter" count={newsletter.length} testId="tab-newsletter" />
          <Tab active={tab === 'bookings'} onClick={() => setTab('bookings')} icon={Calendar} label="Reservas" count={bookings.length} testId="tab-bookings" />
          <Tab active={tab === 'chats'} onClick={() => setTab('chats')} icon={MessageSquare} label="Conversaciones IA" count={chats.length} testId="tab-chats" />
          <div className="ml-auto relative flex items-center gap-2">
            <button
              onClick={exportCurrent}
              className="btn-ghost"
              title="Exportar CSV"
              data-testid="admin-export-csv"
            >
              <Download size={14} /> CSV
            </button>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar..."
                className="field pl-9 w-56"
                data-testid="admin-search"
              />
            </div>
          </div>
        </div>

        {/* Table area */}
        <div className="rounded-2xl border border-white/10 bg-[rgb(var(--bg-1))] overflow-hidden">
          {loading && <div className="p-10 text-center text-white/50"><Loader2 size={20} className="animate-spin inline mr-2" />Cargando...</div>}

          {!loading && tab === 'contacts' && (
            <Table
              cols={['Fecha', 'Nombre', 'Email', 'Empresa', 'Mensaje', 'Lang']}
              rows={filtered.contacts.map((x) => ({
                key: x.id || x.email,
                cells: [
                  formatDate(x.created_at), x.name, x.email, x.company || '—',
                  <span className="line-clamp-2 max-w-[280px] inline-block">{x.message}</span>,
                  <span className="chip">{x.language || 'es'}</span>,
                ],
              }))}
              empty="Sin contactos aún."
            />
          )}

          {!loading && tab === 'newsletter' && (
            <Table
              cols={['Fecha', 'Email', 'Lang']}
              rows={filtered.newsletter.map((x) => ({
                key: x.id || x.email,
                cells: [formatDate(x.created_at), x.email, x.language || 'es'],
              }))}
              empty="Sin suscriptores."
            />
          )}

          {!loading && tab === 'bookings' && (
            <Table
              cols={['Fecha', 'Servicio', 'Día', 'Hora', 'Nombre', 'Email', 'Estado']}
              rows={filtered.bookings.map((x) => ({
                key: x.id || `${x.email}-${x.date}-${x.time}`,
                cells: [
                  formatDate(x.created_at), x.service, x.date, x.time, x.name, x.email,
                  <span className="chip"><span className="dot" />{x.status || 'pending'}</span>,
                ],
              }))}
              empty="Sin reservas."
            />
          )}

          {!loading && tab === 'chats' && (
            <Table
              cols={['Última actividad', 'Sesión', 'Mensajes', 'Lang', 'Primer mensaje', 'Acciones']}
              rows={filtered.chats.map((x) => ({
                key: x.session_id,
                cells: [
                  formatDate(x.last_ts),
                  <code className="text-xs">{x.session_id.slice(0, 14)}…</code>,
                  x.count,
                  <span className="chip">{x.lang}</span>,
                  <span className="line-clamp-2 max-w-[280px] inline-block text-white/70">{x.first_message}</span>,
                  <button onClick={() => openChat(x.session_id)} className="btn-ghost !py-1.5 !px-3 text-xs">
                    <Eye size={12} /> Ver
                  </button>,
                ],
              }))}
              empty="Sin conversaciones."
            />
          )}
        </div>

        {/* Chat detail modal */}
        {chatDetail && (
          <div className="fixed inset-0 z-[60] grid place-items-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setChatDetail(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-2xl max-h-[80vh] rounded-2xl border border-white/10 bg-[rgb(var(--bg-1))] overflow-hidden flex flex-col"
            >
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
                <div>
                  <div className="kicker-mono text-white/50">// Conversación</div>
                  <code className="text-xs text-white/80">{chatDetail.session_id}</code>
                </div>
                <button onClick={() => setChatDetail(null)} className="h-8 w-8 grid place-items-center rounded-full hover:bg-white/5 text-white/70">
                  <X size={16} />
                </button>
              </div>
              <div className="p-5 overflow-y-auto space-y-3">
                {chatLoading && <div className="text-center text-white/50"><Loader2 size={16} className="animate-spin inline mr-2" />Cargando...</div>}
                {!chatLoading && chatDetail.messages.map((m, i) => (
                  <div key={`${chatDetail.session_id}-msg-${m.ts || i}`} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === 'user' ? 'bg-[rgb(var(--brand-electric))] text-white rounded-br-sm' : 'bg-[rgb(var(--bg-2))] text-white/90 border border-white/10 rounded-bl-sm'
                    }`}>
                      {m.content}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </main>
  );
};

const Table = ({ cols, rows, empty }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-[rgb(var(--bg-2))] text-white/60">
        <tr>
          {cols.map((c) => (
            <th key={c} className="text-left font-mono-accent text-[10px] uppercase tracking-wider px-4 py-3">{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={cols.length} className="px-4 py-10 text-center text-white/50">{empty}</td></tr>
        ) : rows.map((row) => (
          <tr key={row.key} className="border-t border-white/5 hover:bg-white/[0.02] transition-colors">
            {row.cells.map((cell, j) => (
              <td key={`${row.key}-${j}`} className="px-4 py-3 text-white/85 align-top">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AdminPage = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem(TOKEN_KEY) || null;
    } catch (err) {
      console.warn('[Admin] localStorage read failed:', err?.message || err);
      return null;
    }
  });
  const [validating, setValidating] = useState(!!token);

  // Validate JWT against backend on mount (auto-logout if expired/invalid).
  // We deliberately run this only once on mount — login/logout are handled
  // by setToken/onLogout below, not by this validator.
  useEffect(() => {
    let active = true;
    if (!token) { setValidating(false); return; }
    adminMe(token)
      .then(() => { if (active) setValidating(false); })
      .catch((err) => {
        if (!active) return;
        console.warn('[Admin] token validation failed:', err?.response?.status, err?.response?.data?.detail || err?.message);
        try { localStorage.removeItem(TOKEN_KEY); } catch (storageErr) {
          console.warn('[Admin] localStorage cleanup failed:', storageErr?.message || storageErr);
        }
        setToken(null);
        setValidating(false);
        toast.error('Sesión expirada. Inicia sesión de nuevo.');
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogout = () => {
    try { localStorage.removeItem(TOKEN_KEY); } catch (err) {
      console.warn('[Admin] localStorage cleanup failed:', err?.message || err);
    }
    setToken(null);
    navigate('/admin');
  };

  if (validating) {
    return (
      <main className="min-h-[80vh] grid place-items-center" data-testid="admin-validating">
        <div className="text-white/60 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Validando sesión…</div>
      </main>
    );
  }

  if (!token) return (<>
    <SEO lang="es" path="/admin" title="Acceso administrativo" description="Cauratech Admin · acceso restringido" noindex />
    <LoginCard onAuth={setToken} />
  </>);
  return (<>
    <SEO lang="es" path="/admin" title="Panel administrativo" description="Cauratech Admin Dashboard" noindex />
    <Dashboard token={token} onLogout={onLogout} />
  </>);
};

export default AdminPage;
